import { formatMoney } from '@shopify/theme-currency';

class KlaviyoBundleTracker {
  constructor() {
    this.sessionId = null;
    this.hasStarted = false;
    this.hasCompleted = false;
    this.latestEventDetail = null;
    this.idleTimeout = null;
    this.idleDelayMs = 5 * 60 * 1000; // 5 minutes idle = abandoned
    this.identifyUser();

    document.addEventListener('bundle:interaction', (e) => this.onInteraction(e));
    document.addEventListener('bundle:completed', (e) => this.onCompleted(e));

    window.addEventListener('beforeunload', () => this.onPageExit());
  }

  identifyUser() {
    if (typeof klaviyo === 'undefined') return;

    const email = window.klaviyoBundleSettings?.customerEmail;
    if (email) klaviyo.identify({ $email: email });
  }

  generateSessionId() {
    return `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  onInteraction(event) {
    this.latestEventDetail = event.detail;

    if (!this.hasStarted) {
      this.sessionId = this.generateSessionId();
      this.hasStarted = true;
    }

    this.resetIdleTimer();
  }

  resetIdleTimer() {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }

    this.idleTimeout = setTimeout(() => {
      this.onIdleTimeout();
    }, this.idleDelayMs);
  }

  onIdleTimeout() {
    if (!this.hasStarted || this.hasCompleted) return;
    if (!this.latestEventDetail) return;

    this.trackAbandoned('idle_timeout');
  }

  onCompleted(event) {
    this.hasCompleted = true;
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    this.track('Bundle Configurator Completed', event.detail);
  }

  onPageExit() {
    if (!this.hasStarted || this.hasCompleted) return;
    if (!this.latestEventDetail) return;

    this.trackAbandoned('page_exit', true);
  }

  trackAbandoned(reason, keepalive = false) {
    if (this.hasCompleted) return;
    this.hasCompleted = true;

    const data = { ...this.latestEventDetail, abandonReason: reason };
    this.track('Bundle Configurator Abandoned', data, keepalive);
  }

  track(eventName, data, keepalive = false) {
    const publicKey = window.klaviyoBundleSettings?.publicKey;
    if (!publicKey) return;

    const payload = this.buildPayload(data);
    const email = window.klaviyoBundleSettings?.customerEmail;

    const trackData = {
      token: publicKey,
      event: eventName,
      customer_properties: email ? { $email: email } : {},
      properties: payload,
    };

    const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(trackData))));

    if (keepalive) {
      console.log('📊 Klaviyo keepalive request:', eventName, payload);
    }

    fetch(`https://a.klaviyo.com/api/track?data=${encodedData}`, {
      method: 'GET',
      keepalive,
    });
  }

  buildPayload(data) {
    const settings = window.klaviyoBundleSettings || {};
    const selections = data.selections || {};
    const actualSelections = Object.values(selections).filter((s) => s !== null);
    const itemCount = actualSelections.length;

    const subtotal = actualSelections.reduce((sum, s) => sum + (s.price || 0), 0);
    const compareAtTotal = actualSelections.reduce((sum, s) => sum + (s.compareAtPrice || s.price || 0), 0);

    const bundleSettings = window.pillowConfiguratorSettings || {};
    const discountPercent = itemCount >= 3 ? bundleSettings.discount3 || 0 : itemCount === 2 ? bundleSettings.discount2 || 0 : 0;
    const discountedTotal = subtotal * (1 - discountPercent / 100);

    return {
      BundleID: this.sessionId,
      BundleName: settings.bundleName || 'Pillow Configurator',
      StepReached: data.state?.currentStep || 1,
      TotalSteps: data.config?.steps?.length || 3,
      Timestamp: new Date().toISOString(),
      ConfiguratorURL: window.location.href,
      SelectedProducts: this.formatSelections(selections),
      TotalItemsSelected: itemCount,
      Subtotal: this.formatPrice(subtotal),
      CompareAtTotal: this.formatPrice(compareAtTotal),
      DiscountPercent: discountPercent,
      DiscountedTotal: this.formatPrice(Math.round(discountedTotal)),
      BundleValue: this.formatPrice(data.bundleValue || discountedTotal),
      DiscountApplied: data.discount || discountPercent,
      ...(data.abandonReason && { AbandonReason: data.abandonReason }),
    };
  }

  formatSelections(selections) {
    return Object.entries(selections)
      .filter(([, sel]) => sel !== null)
      .map(([step, sel]) => ({
        Step: parseInt(step),
        ProductID: sel.productId,
        VariantID: sel.variantId,
        ProductName: sel.title,
        VariantName: sel.variantTitle,
        Price: this.formatPrice(sel.price),
        CompareAtPrice: sel.compareAtPrice ? this.formatPrice(sel.compareAtPrice) : null,
        ImageURL: this.formatImageUrl(sel.image),
      }));
  }

  formatPrice(cents) {
    if (!cents) return null;
    const moneyFormat = window.pillowConfiguratorSettings?.moneyFormat || '€{{amount_with_comma_separator}}';
    return formatMoney(cents, moneyFormat);
  }

  formatImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return 'https:' + url;

    return url;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new KlaviyoBundleTracker());
} else {
  new KlaviyoBundleTracker();
}
